import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';

import { isArray, isDate, isString, omit, trim } from 'lodash-es';
import { TreeNode } from '@openng/optimus-ui/api';
import { TableLazyLoadEvent } from '@openng/optimus-ui/table';

import { ElementRef, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { isAfter, parse, startOfToday } from 'date-fns';
import { DEFAULT_SORTING_TYPE } from '../constants/app.constants';
import { StatusLabel } from '../models';
import { NotificationsService } from '../services/notifications.service';

let cachedInjector: EnvironmentInjector | null = null;

/**
 * Nastaví injektor prostředí aplikace.
 * @param injector Injektor prostředí.
 */
export function setAppEnvironmentInjector(injector: EnvironmentInjector) {
  cachedInjector = injector;
}

export const getOptionsFromStatusLabels = (labels: Record<string, StatusLabel>) => {
  return Object.keys(labels).map((k) => ({ id: k, label: labels[k].label ?? '' }));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getLabelsFromInputArray = (inputArray: any[], labels: Record<string, StatusLabel>): string[] => {
  return inputArray.map((item) => labels[item]?.label ?? item);
};

export const allowedDatepickerKeys = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
  'Backspace',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'Delete',
  'Home',
  'End',
];

export const removeQueryParam = (url: string, parameter: string) => {
  const urlparts = url.split('?');

  if (urlparts.length >= 2) {
    const prefix = encodeURIComponent(parameter) + '=';
    const params = urlparts[1].split(/[&;]/g);

    for (let i = params.length; i-- > 0;) {
      if (params[i].lastIndexOf(prefix, 0) !== -1) {
        params.splice(i, 1);
      }
    }

    return urlparts[0] + (params.length > 0 ? '?' + params.join('&') : '');
  }
  return url;
};

export const getQueryParam = (queryParamUrl: string, paramName: string) => {
  const params = new URLSearchParams(queryParamUrl);
  const param = params.get(paramName);

  return param;
};

export const isDateTodayOrPast = (dateString: string | undefined, format = 'yyyy-MM-dd'): boolean => {
  if (!dateString) return false;

  const date = parse(dateString, format, new Date());

  if (!date || isNaN(date.getTime())) {
    console.error('Invalid date or format:', dateString, format);
    return false;
  }

  const today = startOfToday();

  // Returns true if date is before or same as today
  return !isAfter(date, today);
};

export const getSortFieldParam = (evt: TableLazyLoadEvent, defaultSortType = DEFAULT_SORTING_TYPE) => {
  let sorting = defaultSortType;
  if (evt.sortField) sorting = (evt.sortOrder && evt.sortOrder < 0 ? '-' : '') + evt.sortField;
  return sorting;
};

/**
 * Decides whether the user has access to the given context
 * @param user
 * @param context
 * @param rolesToMatch
 * @returns
 */

export const isObject = (v: unknown) => {
  return v instanceof Object && !Array.isArray(v) && v !== null;
};

/**
 * Transform empty strings to NULL in passed object
 */
export const emptyStringsToNull = <T>(data: Record<string, unknown>) => {
  return Object.keys(data).reduce((acc, key) => {
    let v = data[key];

    if (isString(v) && (v as string).length === 0) {
      v = null;
    } else if (isObject(v) && !isDate(v)) {
      v = emptyStringsToNull(v as Record<string, unknown>);
    } else if (isArray(v)) {
      v = v.map((arrayValue) => (isObject(arrayValue) ? emptyStringsToNull(arrayValue) : arrayValue));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (acc as any)[key] = isString(v) ? trim(v) : v;
    return acc;
  }, {} as T);
};

/**
 * Zkopíruje text do schránky (Clipboard API; prohlížeč ho povoluje jen na HTTPS a localhost).
 */
export const copyToClipboard = (payload: string): Promise<void> => navigator.clipboard.writeText(payload);

export const flattenTreeNodes = (nodes: TreeNode[]): TreeNode[] => {
  return nodes.reduce((acc, node) => {
    acc.push(omit(node, ['parent', 'children']));

    acc = [...acc, ...flattenTreeNodes((node.children as TreeNode[]) || [])];
    return acc;
  }, [] as TreeNode[]);
};

export const decodeRFC5987Value = (value: string): string => {
  if (value.startsWith("UTF-8''")) {
    value = value.substring(7); // Remove "UTF-8''"
  }
  return decodeURIComponent(value);
};

export function scrollToActivePanel(element: ElementRef, block = 'start') {
  if (element) {
    element.nativeElement.scrollIntoView({ behavior: 'smooth', block: block });
  }
}

/**
 * Function to iterate through form controls and find errors,
 * if control has errors, looks for associated element to it the control,
 * if found by formControlName attribute, scrolls to it.
 *
 * @param formToInvestigate usually this.filterForm
 *
 * @example
 * public submitForm(): void {
 *   if (this.filterForm.invalid) {
 *     scrollToFirstErrorInput(this.filterForm);
 *   }
 * }
 *
 * @requires
 * The form control must be explicitly marked as invalid by Angular
 * Invalid `FormGroup` or `ng-container` can't be scrolled to, because they are not part of the DOM
 */
export function scrollToFirstErrorInput(formToInvestigate: FormGroup): void {
  // function to find errors in form
  const findErrorsInForm = (formToInvestigate: FormGroup): { errors: ValidationErrors; element: HTMLElement }[] => {
    const errors: { errors: ValidationErrors; element: HTMLElement }[] = [];

    const recursiveFunc = (form: FormGroup, parentPath = '') => {
      Object.keys(form.controls).forEach((field) => {
        const control = form.get(field);
        const fieldPath = parentPath ? `${parentPath}.${field}` : field;

        if (control?.errors) {
          const fieldName = fieldPath.split('.').pop() || fieldPath;
          const element = document.querySelector(`[formControlName="${fieldName}"]`) as HTMLElement;
          const elements = Array.from(document.querySelectorAll(`[formControlName="${fieldName}"]`)) as HTMLElement[];

          if (elements.length > 1) {
            // console.warn('Multiple elements found for formControlName:', fieldName);
            elements.forEach((element) => {
              control.errors && errors.push({ errors: control.errors, element });
            });
          } else if (element) {
            errors.push({ errors: control.errors, element });
          } else {
            // console.warn('Element not found for formControlName:', fieldName);
          }
        }

        if (control instanceof FormGroup) {
          recursiveFunc(control, fieldPath);
        }

        if (control instanceof FormArray) {
          control.controls.forEach((control) => {
            if (control instanceof FormGroup) {
              recursiveFunc(control, fieldPath);
            }
          });
        }
      });
    };

    recursiveFunc(formToInvestigate);
    return errors;
  };

  // function to scroll to the first error input
  const scrollToErrorInput = (element: HTMLElement) => element.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // find errors and scroll to the first one
  const errors = findErrorsInForm(formToInvestigate);
  if (errors.length > 0) {
    scrollToErrorInput(errors[0].element);
  }
}

export function getUniqueRandomNumber(): number {
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 1000);
  return parseInt(`${timestamp}${randomNum}`);
}

export function restrictDatepickerInput(event: KeyboardEvent) {
  const allowedKeys = allowedDatepickerKeys;
  const ctrlKeyPressed = event.ctrlKey || event.metaKey;

  if (ctrlKeyPressed) {
    switch (event.key) {
      case 'a':
      case 'c':
      case 'v':
      case 'x':
        return;
    }
  }

  if (allowedKeys.indexOf(event.key) === -1) {
    event.preventDefault();
  }
}

export function sortByAttributes<T>(array: T[], attributes: (keyof T)[], directions?: ('asc' | 'desc')[]): T[] {
  return array.sort((a, b) => {
    for (let i = 0; i < attributes.length; i++) {
      const attribute = attributes[i];
      const direction = directions?.[i] || 'asc';

      const valueA = a[attribute];
      const valueB = b[attribute];

      let comparison: number;

      // Handle different value types
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparison = valueA - valueB;
      } else if (valueA instanceof Date && valueB instanceof Date) {
        comparison = valueA.getTime() - valueB.getTime();
      } else {
        const strA = valueA?.toString().toLowerCase() || '';
        const strB = valueB?.toString().toLowerCase() || '';
        comparison = strA.localeCompare(strB);
      }

      if (comparison !== 0) {
        return direction === 'asc' ? comparison : -comparison;
      }
    }
    return 0;
  });
}

export function findInsertIndex<T>(
  formArray: FormArray,
  newItemValues: Partial<T>,
  attributes: (keyof T)[],
  directions?: ('asc' | 'desc')[]
): number {
  let insertIndex = 0;

  for (let i = 0; i < formArray.length; i++) {
    const existingGroup = formArray.at(i) as FormGroup;
    let comparisonResult = 0;

    for (let j = 0; j < attributes.length; j++) {
      const attribute = attributes[j];
      const direction = directions?.[j] || 'asc';

      const newValue = newItemValues[attribute];
      const existingValue = existingGroup.get(attribute as string)?.value;

      if (typeof newValue === 'number' && typeof existingValue === 'number') {
        comparisonResult = newValue - existingValue;
      } else if (newValue instanceof Date && existingValue instanceof Date) {
        comparisonResult = newValue.getTime() - existingValue.getTime();
      } else {
        const newValueStr = (newValue as string | number | boolean | null | undefined)?.toString().toLowerCase() || '';
        const existingValueStr =
          (existingValue as string | number | boolean | null | undefined)?.toString().toLowerCase() || '';
        comparisonResult = newValueStr.localeCompare(existingValueStr);
      }

      if (comparisonResult !== 0) {
        if (direction === 'desc') {
          comparisonResult = -comparisonResult;
        }
        break;
      }
    }

    if (comparisonResult < 0) {
      break;
    } else {
      insertIndex++;
    }
  }

  return insertIndex;
}

export function findFirstInvalidControl(
  control: AbstractControl,
  path: (string | number)[] = []
): { control: AbstractControl; path: (string | number)[] } | null {
  if (control.invalid && (control.dirty || control.touched)) {
    if (control instanceof FormGroup) {
      for (const key of Object.keys(control.controls)) {
        const childControl = control.get(key);
        if (childControl) {
          const result = findFirstInvalidControl(childControl, [...path, key]);
          if (result) {
            return result;
          }
        }
      }
    } else if (control instanceof FormArray) {
      for (let i = 0; i < control.length; i++) {
        const childControl = control.at(i);
        const result = findFirstInvalidControl(childControl, [...path, i]);
        if (result) {
          return result;
        }
      }
    } else {
      // It's an invalid FormControl
      return { control, path };
    }
  }

  return null;
}

export function showSuccessMessage(message: string, life = 4000, notificationsService?: NotificationsService): void {
  if (notificationsService) {
    notificationsService.dispatchSuccess(message, { life });
  } else if (cachedInjector) {
    runInInjectionContext(cachedInjector, () => {
      const service = inject(NotificationsService);
      service.dispatchSuccess(message, { life });
    });
  } else {
    console.warn('NotificationsService is not available in current context.');
  }
}

export function findMaxDate(notInPast: boolean, date: Date | null): Date | null {
  if (!date) return notInPast ? new Date() : null;

  const today = new Date();
  return notInPast ? (today > date ? today : date) : date;
}

export function findMaxDateMinTomorrow(notInPast: boolean, date: Date | null): Date | null {
  if (!notInPast) {
    return date;
  }

  const tomorrow = new Date();
  // const tomorrow = new Date(2025, 10, 30); // for test purposes
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (!date) return tomorrow;

  return tomorrow > date ? tomorrow : date;
}

export function encodeBase64WithCRLF(input: string) {
  // Ensure Windows-style line endings (CRLF)
  const normalizedInput = input.replace(/\r?\n/g, '\r\n');

  // Convert to Base64
  const encoded = btoa(String.fromCharCode(...new TextEncoder().encode(normalizedInput)));

  return encoded;
}

/**
 * Přidá jednu nebo více hodnot do pole, pokud tam ještě nejsou.
 * @param list Výchozí pole.
 * @param value Jedna hodnota nebo pole hodnot.
 * @returns Nové pole s přidanými hodnotami (unikátními).
 */
export function addUniqueValue<T>(list: T[], value: T | T[]): T[] {
  const valuesToAdd = Array.isArray(value) ? value : [value];
  return [...list, ...valuesToAdd.filter((v) => !list.includes(v))];
}

/**
 * Odebere jednu nebo více hodnot z pole, pokud tam jsou.
 * @param list Výchozí pole.
 * @param value Jedna hodnota nebo pole hodnot.
 * @returns Nové pole bez odebraných hodnot.
 */
export function removeValue<T>(list: T[], value: T | T[]): T[] {
  const valuesToRemove = Array.isArray(value) ? value : [value];
  return list.filter((v) => !valuesToRemove.includes(v));
}
